import { useQuery } from '@tanstack/react-query';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import { queries } from '../../../queries';
import { getMessageByMsgId } from '../../../api/cmail.api';

export function useGetCmailMessage(msgId: string) {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    ...queries.cmail.detail(msgId),
    keepPreviousData: true,
    onError: handleAPIError('Failed to fetch message'),
    enabled: Boolean(msgId),
    staleTime: 2 * 60 * 1000,
  });
}

export function useGetCmailMessages(msgIds: string[]) {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    ...queries.cmail.batchDetails(msgIds),
    enabled: msgIds.length > 0,
    onError: handleAPIError('Failed to fetch thread messages'),
  });
}

/** Prefix shared by every recursive thread query, so refresh can invalidate them all. */
export const CMAIL_THREAD_QUERY_KEY = ['cmail', 'thread', 'recursive'] as const;

export const getCmailThreadQueryKey = (
  rootMessageId: string,
  maxDepth: number,
  maxMessages: number
) => [...CMAIL_THREAD_QUERY_KEY, rootMessageId, maxDepth, maxMessages];

// Recursive thread fetching with depth control
export function useGetCmailThread(
  rootMessageId: string,
  maxDepth = 3,
  maxMessages = 20
) {
  const handleAPIError = useAPIErrorHandler();

  return useQuery({
    queryKey: getCmailThreadQueryKey(rootMessageId, maxDepth, maxMessages),
    queryFn: async () => {
      const threadMessages = new Map<string, any>();
      const processedIds = new Set<string>();
      const failedIds: string[] = [];

      const fetchMessage = async (msgId: string) => {
        try {
          const resp = await getMessageByMsgId({ params: { msgId } });
          return resp.data;
        } catch {
          return null;
        }
      };

      let currentBatch = [{ msgId: rootMessageId, depth: 0 }];

      while (currentBatch.length > 0 && threadMessages.size < maxMessages) {
        const toFetch = currentBatch.filter(
          ({ msgId, depth }) => !processedIds.has(msgId) && depth < maxDepth
        );

        toFetch.forEach(({ msgId }) => processedIds.add(msgId));

        const results = await Promise.allSettled(
          toFetch.map(async ({ msgId, depth }) => {
            const message = await fetchMessage(msgId);
            return { msgId, depth, message };
          })
        );

        const nextBatch: typeof currentBatch = [];

        for (let resultIndex = 0; resultIndex < results.length; resultIndex++) {
          const result = results[resultIndex];
          if (result.status !== 'fulfilled') {
            failedIds.push(toFetch[resultIndex].msgId);
            continue;
          }
          const { msgId, depth, message } = result.value;

          if (!message) {
            failedIds.push(msgId);
            continue;
          }

          threadMessages.set(msgId, { ...message, depth });

          if (depth < maxDepth - 1 && message.references) {
            message.references
              .filter(
                (refId: string) =>
                  !processedIds.has(refId) && !threadMessages.has(refId)
              )
              .forEach((refId: string) => {
                nextBatch.push({ msgId: refId, depth: depth + 1 });
              });
          }
        }

        currentBatch = nextBatch.slice(0, maxMessages - threadMessages.size);
      }

      const messages = Array.from(threadMessages.values()).sort(
        (a, b) =>
          new Date(b.dateReceived).getTime() -
          new Date(a.dateReceived).getTime()
      );

      return {
        messages,
        failedIds,
        totalProcessed: processedIds.size,
        depthReached:
          messages.length > 0 ? Math.max(...messages.map((m) => m.depth)) : 0,
      };
    },
    enabled: Boolean(rootMessageId),
    onError: handleAPIError('Failed to fetch email thread'),
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
}
